import type { FC, ReactNode } from 'react'

import { createElement } from 'react'
import { cn } from '@/lib/utils'

interface HeroTitleProps {
    text: string
    as?: 'h1' | 'h2' | 'p'
    className?: string
    accent?: string[]
    accentClassName?: string
    baseDelay?: number
    step?: number
}

/**
 * Word-by-word masked reveal driven by CSS, so it plays on the very first paint
 * with no flash, and waits for the page transition through --enter-delay.
 */
export const HeroTitle: FC<HeroTitleProps> = ({
    text,
    as = 'h1',
    className,
    accent = [],
    accentClassName = 'text-ember',
    baseDelay = 120,
    step = 55
}): ReactNode => {
    const words = text.split(' ')

    const content = words.map((word, index) => (
        <span key={`${word}-${index}`}>
            <span className='-mb-[0.14em] inline-block overflow-hidden pb-[0.14em] align-bottom'>
                <span
                    className={cn('inline-block animate-word-up', accent.includes(word) && accentClassName)}
                    style={{ animationDelay: `calc(var(--enter-delay, 0ms) + ${baseDelay + index * step}ms)` }}
                >
                    {word}
                </span>
            </span>

            {index < words.length - 1 && ' '}
        </span>
    ))

    return createElement(as, { className }, content)
}

interface EnterProps {
    children: ReactNode
    className?: string
    delay?: number
}

export const Enter: FC<EnterProps> = ({ children, className, delay = 0 }): ReactNode => {
    return (
        <div
            className={cn('animate-fade-up', className)}
            style={{ animationDelay: `calc(var(--enter-delay, 0ms) + ${delay}ms)` }}
        >
            {children}
        </div>
    )
}
